import { useCallback, useEffect, useRef } from 'react';
import type {
  DefaultValues,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useStableRef } from './useStableRef.ts';

export type FieldMetaInfos<FormValues extends FieldValues> = {
  [K in keyof FormValues]: {
    debounce: boolean;
    ignorePropertiesForComparison?: string[];
  };
};

interface UseAutoSaveFormParams<SourceData, FormValues extends FieldValues> {
  sourceData: SourceData;
  getDefaultValues: (data: SourceData) => DefaultValues<FormValues>;
  save: (data: FormValues) => Promise<SourceData>;
  fieldMetaInfos: FieldMetaInfos<FormValues>;
  onValidationFailed: () => void;
  onValidationPassed: () => void;
  saveMode?: 'change' | 'blur';
  validationMode?: 'onBlur' | 'onChange';
}

export function useAutoSaveForm<SourceData, FormValues extends FieldValues>({
  sourceData,
  getDefaultValues,
  save,
  fieldMetaInfos,
  onValidationFailed,
  onValidationPassed,
  saveMode = 'change',
  validationMode = 'onBlur',
}: UseAutoSaveFormParams<SourceData, FormValues>) {
  const saveRef = useStableRef(save);
  const onValidationPassedRef = useStableRef(onValidationPassed);
  const onValidationFailedRef = useStableRef(onValidationFailed);
  const saveInProgress = useRef(false);
  const saveOnUpdateSuccess = useRef(false);
  const isApplyingServerDefaults = useRef(false);
  const cancelSaveOnUpdateSuccess = useCallback(() => (saveOnUpdateSuccess.current = false), []);
  const shouldIgnoreSaveTriggeredByDefaults = useCallback(() => isApplyingServerDefaults.current, []);

  const {
    register,
    control,
    getFieldState,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: {
      errors,
      isDirty: isFormDirty,
      dirtyFields: _dirtyFields, // we need to subscribe to dirty fields, otherwise we don't get updates
    },
    reset,
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(sourceData),
    mode: validationMode,
    reValidateMode: validationMode,
  });

  const markSavedValuesAsBaselineRef = useStableRef((savedValues: FormValues) => {
    isApplyingServerDefaults.current = true;

    try {
      reset(savedValues, {
        keepErrors: true,
        keepTouched: true,
        keepValues: true,
      });
    } finally {
      isApplyingServerDefaults.current = false;
    }
  });

  const scheduleSave = useCallback(() => {
    if (saveInProgress.current) {
      saveOnUpdateSuccess.current = true;
      return;
    }
    saveInProgress.current = true;
    void handleSubmit(
      async (data: FormValues) => {
        onValidationPassedRef.current();
        try {
          await saveRef.current(data);
          markSavedValuesAsBaselineRef.current(data);
          saveInProgress.current = false;
          if (saveOnUpdateSuccess.current) {
            saveOnUpdateSuccess.current = false;
            scheduleSave();
          }
        } catch {
          saveInProgress.current = false;
        }
      },
      () => {
        onValidationFailedRef.current();
        saveInProgress.current = false;
      },
    )();
  }, [handleSubmit]);

  const registerWithSaveOnBlur = useCallback<UseFormRegister<FormValues>>(
    <TFieldName extends Path<FormValues>>(name: TFieldName, options?: RegisterOptions<FormValues, TFieldName>) => {
      const fieldRegistration = register(name, options);

      return {
        ...fieldRegistration,
        onBlur: async (event: Parameters<typeof fieldRegistration.onBlur>[0]) => {
          await fieldRegistration.onBlur(event);
          scheduleSave();
        },
      };
    },
    [register, scheduleSave],
  );

  const handleSaveOnBlur = useCallback(
    (onBlur: () => void) => {
      onBlur();
      scheduleSave();
    },
    [scheduleSave],
  );

  const resetToSourceData = useCallback(
    (updatedData: SourceData) => {
      reset(getDefaultValues(updatedData));
    },
    [getDefaultValues, reset],
  );

  useScheduleSaveOnFormChanges(
    watch,
    fieldMetaInfos,
    scheduleSave,
    cancelSaveOnUpdateSuccess,
    shouldIgnoreSaveTriggeredByDefaults,
    saveMode === 'change',
  );
  useScheduleSaveOnFormTurningValid(errors, scheduleSave);

  return {
    register,
    control,
    getFieldState,
    setValue,
    getValues,
    watch,
    formState: { errors, isFormDirty },
    scheduleSave,
    registerWithSaveOnBlur,
    handleSaveOnBlur,
    resetToSourceData,
  };
}

function useScheduleSaveOnFormTurningValid<FormValues extends FieldValues>(
  errors: FieldErrors<FormValues>,
  scheduleSave: () => void,
) {
  const isFormValid = !Object.keys(errors).length;
  const isFormValidRef = useRef(isFormValid);
  if (isFormValid && !isFormValidRef.current) {
    scheduleSave();
  }
  isFormValidRef.current = isFormValid;
}

function useScheduleSaveOnFormChanges<FormValues extends FieldValues>(
  watch: UseFormWatch<FormValues>,
  fieldMetaInfos: FieldMetaInfos<FormValues>,
  scheduleSave: () => void,
  cancelSaveOnUpdateSuccess: () => void,
  shouldIgnoreChange: () => boolean,
  enabled: boolean,
) {
  const scheduledSaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cleanupScheduledBlurSave = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      clearTimeout(scheduledSaveTimer.current);
      cleanupScheduledBlurSave.current?.();
      cleanupScheduledBlurSave.current = undefined;
      return;
    }

    const subscription = watch((_data, { name }) => {
      if (shouldIgnoreChange()) {
        return;
      }

      if (!name || !fieldMetaInfos[name]) {
        return;
      }
      clearTimeout(scheduledSaveTimer.current);
      cleanupScheduledBlurSave.current?.();
      cleanupScheduledBlurSave.current = undefined;
      cancelSaveOnUpdateSuccess();
      if (fieldMetaInfos[name].debounce) {
        const focusedField = getFocusedField(name);
        if (focusedField) {
          focusedField.addEventListener('blur', scheduleSave, { once: true });
          cleanupScheduledBlurSave.current = () => focusedField.removeEventListener('blur', scheduleSave);
          return;
        }

        scheduledSaveTimer.current = setTimeout(scheduleSave, 3000);
      } else {
        scheduleSave();
      }
    });

    return () => {
      clearTimeout(scheduledSaveTimer.current);
      cleanupScheduledBlurSave.current?.();
      cleanupScheduledBlurSave.current = undefined;
      subscription.unsubscribe();
    };
  }, [watch, scheduleSave, fieldMetaInfos, cancelSaveOnUpdateSuccess, shouldIgnoreChange, enabled]);
}

function getFocusedField(fieldName: string) {
  const activeElement = document.activeElement;

  return activeElement instanceof HTMLElement && activeElement.getAttribute('name') === fieldName
    ? activeElement
    : null;
}
